import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { brl, fmtDate } from "../lib/format";
import { ArrowClockwise, MagnifyingGlass, CaretDown, WhatsappLogo, InstagramLogo, Warning, FilePdf, FileXls, Copy, CheckCircle, CreditCard, Money, XCircle } from "@phosphor-icons/react";
import LazyImage from "../components/LazyImage";
import WithdrawReasonModal from "../components/WithdrawReasonModal";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function PendingByHandleReport() {
  const [view, setView] = useState("pending"); // 'pending' | 'withdrawn'
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({});
  const [copying, setCopying] = useState(false);
  const [marking, setMarking] = useState(null); // key: `${handle}` or `${handle}:${live_id}`
  const [withdrawModal, setWithdrawModal] = useState(null); // { handle, live_id, total, reason }

  const openWithdraw = ({ handle, live_id, total }) => {
    setWithdrawModal({ handle, live_id: live_id || null, total: total || 0, reason: "" });
  };
  const closeWithdraw = () => setWithdrawModal(null);
  const confirmWithdraw = async () => {
    if (!withdrawModal) return;
    const { handle, live_id, total, reason } = withdrawModal;
    const key = live_id ? `${handle}:${live_id}` : handle;
    setMarking(key);
    try {
      const payload = { handle, payment_method: "withdrawn" };
      if (live_id) payload.live_ids = [live_id];
      const trimmed = (reason || "").trim();
      if (trimmed) payload.reason = trimmed;
      const { data } = await api.post("/reports/pending-by-handle/mark-paid", payload);
      toast.success(`${data.modified} desistência(s) registrada(s)${trimmed ? ` — ${trimmed}` : ""}`);
      closeWithdraw();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao registrar desistência");
    } finally { setMarking(null); }
    void total;
  };

  const markPaid = async ({ handle, live_id, method, total }) => {
    if (method === "withdrawn") {
      openWithdraw({ handle, live_id, total });
      return;
    }
    const scope = live_id ? "esta live" : "TODAS as lives em aberto";
    const label = method === "pix" ? "Pix" : "Cartão";
    const money = total ? ` de ${brl(total)}` : "";
    if (!window.confirm(`Marcar ${scope} de ${handle}${money} como paga(s) via ${label}?`)) return;
    const key = live_id ? `${handle}:${live_id}` : handle;
    setMarking(key);
    try {
      const payload = { handle, payment_method: method };
      if (live_id) payload.live_ids = [live_id];
      const { data } = await api.post("/reports/pending-by-handle/mark-paid", payload);
      toast.success(`${data.modified} pedido(s) baixado(s) via ${label}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao baixar");
    } finally { setMarking(null); }
  };

  const revertWithdrawn = async ({ handle, live_id, total }) => {
    const scope = live_id ? "esta live" : "TODAS as lives com desistência";
    const money = total ? ` de ${brl(total)}` : "";
    if (!window.confirm(`Reverter desistência de ${scope} para ${handle}${money}? Voltará para A Receber.`)) return;
    const key = `revert:${live_id || ""}:${handle}`;
    setMarking(key);
    try {
      const payload = { handle };
      if (live_id) payload.live_ids = [live_id];
      const { data } = await api.post("/reports/pending-by-handle/revert-withdrawn", payload);
      toast.success(`${data.modified} pedido(s) voltaram para A Receber`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao reverter");
    } finally { setMarking(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = view === "withdrawn"
        ? "/reports/withdrawn-by-handle"
        : "/reports/pending-by-handle";
      const { data } = await api.get(endpoint, { params: { _: Date.now() } });
      setRows(data);
      setExpanded({});
      setSelected({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar relatório");
    } finally { setLoading(false); }
  }, [view]);
  useEffect(() => { load(); }, [load]);

  const downloadReport = async (kind /* pdf | xlsx */) => {
    try {
      const resp = await api.get(`/reports/pending-by-handle/report.${kind}`, {
        params: { _: Date.now() },
        responseType: "blob",
        headers: { "Cache-Control": "no-cache" },
      });
      const blob = new Blob([resp.data], {
        type: kind === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pendencias-por-arroba.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Relatório ${kind.toUpperCase()} baixado`);
    } catch {
      toast.error(`Falha ao gerar ${kind.toUpperCase()}`);
    }
  };

  const visible = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const filtered = f
      ? rows.filter(r =>
          (r.handle || "").toLowerCase().includes(f) ||
          (r.customer_name || "").toLowerCase().includes(f),
        )
      : rows;
    // Alphabetical order by @arroba (ignoring leading "@" and case-insensitive, pt-BR locale)
    return [...filtered].sort((a, b) =>
      (a.handle || "").replace(/^@/, "").localeCompare((b.handle || "").replace(/^@/, ""), "pt-BR", { sensitivity: "base" }),
    );
  }, [rows, filter]);

  const grand = visible.reduce((s, r) => s + (r.total || 0), 0);
  const selectedHandles = useMemo(
    () => visible.filter(r => selected[r.handle]).map(r => r.handle),
    [visible, selected],
  );
  const allVisibleSelected = visible.length > 0 && visible.every(r => selected[r.handle]);
  const toggleAllVisible = () => {
    const next = { ...selected };
    if (allVisibleSelected) {
      visible.forEach(r => { delete next[r.handle]; });
    } else {
      visible.forEach(r => { next[r.handle] = true; });
    }
    setSelected(next);
  };

  const copySelectedMessages = async () => {
    if (copying) return;
    if (selectedHandles.length === 0) {
      toast.error("Selecione ao menos uma @");
      return;
    }
    setCopying(true);
    try {
      const { data } = await api.post("/reports/pending-by-handle/messages", { handles: selectedHandles });
      if (!Array.isArray(data) || data.length === 0) {
        toast.error("Nenhuma mensagem gerada");
        return;
      }
      const separator = "\n\n" + "─".repeat(24) + "\n\n";
      const text = data.map(m => m.message).join(separator);
      await navigator.clipboard.writeText(text);
      toast.success(`${data.length} cobrança(s) copiada(s) — cole no Direct do Instagram`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao copiar mensagens");
    } finally { setCopying(false); }
  };

  return (
    <div data-testid="pending-by-handle-report" className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="label-brutal">Relatório · Financeiro</p>
          <h1 className="font-heading text-3xl sm:text-5xl font-black">
            {view === "withdrawn" ? "Desistências / Bloqueios" : "Pendências de pagamentos"}
          </h1>
          <p className="text-brand-ink2 mt-2">
            {view === "withdrawn"
              ? <>Clientes que <strong>desistiram / foram bloqueadas</strong>. Use <em>Reverter</em> caso a cliente resolva pagar.</>
              : <>Todas as @arrobas com <strong>lives em aberto</strong>, com valor por live e total consolidado por cliente.</>}
          </p>
          <div className="mt-3 inline-flex border-2 border-brand-ink">
            <button
              data-testid="pending-view-pending"
              onClick={() => setView("pending")}
              className={`px-4 py-2 text-xs font-bold uppercase ${view === "pending" ? "bg-brand-pink text-white" : "bg-white hover:bg-brand-yellow"}`}
            >Em aberto</button>
            <button
              data-testid="pending-view-withdrawn"
              onClick={() => setView("withdrawn")}
              className={`px-4 py-2 text-xs font-bold uppercase border-l-2 border-brand-ink ${view === "withdrawn" ? "bg-brand-peach" : "bg-white hover:bg-brand-yellow"}`}
            >Desistências</button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {view === "pending" && (
            <button
              onClick={copySelectedMessages}
              disabled={selectedHandles.length === 0 || copying}
              className="btn-brutal bg-brand-yellow text-brand-ink hover:bg-brand-peach h-11 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="pending-copy-messages"
            >
              <Copy weight="bold" size={16} />
              {copying ? "Copiando…" : `Copiar cobranças${selectedHandles.length ? ` (${selectedHandles.length})` : ""}`}
            </button>
          )}
          {view === "pending" && (
            <>
              <button onClick={() => downloadReport("pdf")} className="btn-pink h-11" data-testid="pending-download-pdf">
                <FilePdf weight="bold" size={16} /> PDF
              </button>
              <button onClick={() => downloadReport("xlsx")} className="btn-white h-11" data-testid="pending-download-xlsx">
                <FileXls weight="bold" size={16} /> Excel
              </button>
            </>
          )}
          <button onClick={load} className="btn-white h-11" data-testid="pending-refresh">
            <ArrowClockwise weight="bold" size={16} /> Atualizar
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat
          label={view === "withdrawn" ? "@s com desistência" : "@s com pendência"}
          value={visible.length}
          bg={view === "withdrawn" ? "bg-brand-peach" : "bg-brand-peach"}
          icon={view === "withdrawn" ? XCircle : Warning}
        />
        <Stat
          label={view === "withdrawn" ? "Total desistido" : "Total em aberto"}
          value={brl(grand)}
          bg={view === "withdrawn" ? "bg-brand-peach text-brand-ink" : "bg-brand-pink text-white"}
          icon={view === "withdrawn" ? XCircle : Warning}
        />
        <div className="card-brutal p-3 bg-white flex items-center gap-2">
          <MagnifyingGlass weight="bold" size={18} />
          <input data-testid="pending-filter" className="input-brutal !h-10"
            placeholder="Filtrar @ ou nome…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div className="card-brutal p-10 text-center font-bold">Carregando…</div>
      ) : visible.length === 0 ? (
        <div className="card-brutal p-10 text-center bg-brand-mint">
          <p className="font-heading text-xl font-black">
            {view === "withdrawn" ? "Nenhuma desistência!" : "Nada pendente!"}
          </p>
          <p className="text-brand-ink2 text-sm">
            {view === "withdrawn" ? "Nenhuma cliente foi marcada como desistência." : "Não há clientes com lives em aberto."}
          </p>
        </div>
      ) : (
        <div className="card-brutal overflow-x-auto">
          <table className="tbl w-full min-w-[820px]">
            <thead>
              <tr>
                {view === "pending" && (
                  <th className="w-10 text-center">
                    <input
                      type="checkbox"
                      data-testid="pending-select-all"
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                      aria-label="Selecionar todas"
                    />
                  </th>
                )}
                <th className="w-10"></th>
                <th>@ / Cliente</th>
                <th className="w-24 text-right">Lives</th>
                <th className="w-36 text-right">{view === "withdrawn" ? "Total desistido" : "Total em aberto"}</th>
                <th className="w-56">Contato</th>
                <th className="w-56 text-center">{view === "withdrawn" ? "Reverter" : "Baixar tudo"}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(r => {
                const isOpen = !!expanded[r.handle];
                const isSel = !!selected[r.handle];
                return (
                  <React.Fragment key={r.handle}>
                    <tr data-testid={`pending-row-${r.handle}`} className="cursor-pointer" onClick={() => setExpanded(e => ({ ...e, [r.handle]: !e[r.handle] }))}>
                      {view === "pending" && (
                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            data-testid={`pending-select-${r.handle}`}
                            checked={isSel}
                            onChange={() => setSelected(s => ({ ...s, [r.handle]: !s[r.handle] }))}
                            aria-label={`Selecionar ${r.handle}`}
                          />
                        </td>
                      )}
                      <td className="text-center">
                        <CaretDown weight="bold" size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          {r.has_customer_photo && r.customer_id ? (
                            <LazyImage
                              src={`${API_BASE}/api/customers/${r.customer_id}/photo?w=96`}
                              alt=""
                              className="w-8 h-8 rounded-full border-2 border-brand-ink object-cover"
                            />
                          ) : (
                            <span className="w-8 h-8 rounded-full border-2 border-brand-ink bg-brand-cream inline-flex items-center justify-center font-heading font-black text-xs">
                              {((r.customer_name || r.handle || "?")[0] || "?").toUpperCase()}
                            </span>
                          )}
                          <div>
                            <p className="font-bold text-sm">{r.handle}</p>
                            <p className="text-xs text-brand-ink2">{r.customer_name || "sem cadastro"}</p>
                          </div>
                        </div>
                      </td>
                      <td className="text-right">{r.lives_count}</td>
                      <td className="text-right font-bold">{brl(r.total)}</td>
                      <td className="text-xs" onClick={(e) => e.stopPropagation()}>
                        {r.whatsapp && (
                          <a href={`https://wa.me/${(r.whatsapp || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
                            <WhatsappLogo weight="bold" size={14} /> {r.whatsapp}
                          </a>
                        )}<br />
                        <a href={`https://instagram.com/${(r.handle || "").replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
                          <InstagramLogo weight="bold" size={14} /> Instagram
                        </a>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {view === "withdrawn" ? (
                          <div className="flex items-center justify-center gap-1">
                            <button
                              data-testid={`pending-revert-${r.handle}`}
                              disabled={marking === `revert::${r.handle}`}
                              onClick={() => revertWithdrawn({ handle: r.handle, total: r.total })}
                              className="btn-brutal bg-brand-mint text-brand-ink hover:bg-[#C7E5D0] !h-9 !px-3 text-xs disabled:opacity-40"
                              title="Voltar para A Receber"
                            >
                              <ArrowClockwise weight="bold" size={14} /> Reverter tudo
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-1 flex-wrap">
                            <button
                              data-testid={`pending-mark-pix-${r.handle}`}
                              disabled={marking === r.handle}
                              onClick={() => markPaid({ handle: r.handle, method: "pix", total: r.total })}
                              className="btn-brutal bg-brand-mint text-brand-ink hover:bg-[#C7E5D0] !h-9 !px-2 text-xs disabled:opacity-40"
                              title="Baixar via Pix"
                            >
                              <Money weight="bold" size={14} /> Pix
                            </button>
                            <button
                              data-testid={`pending-mark-credit-${r.handle}`}
                              disabled={marking === r.handle}
                              onClick={() => markPaid({ handle: r.handle, method: "credit", total: r.total })}
                              className="btn-brutal bg-brand-yellow text-brand-ink hover:bg-brand-peach !h-9 !px-2 text-xs disabled:opacity-40"
                              title="Baixar via Cartão"
                            >
                              <CreditCard weight="bold" size={14} /> Cartão
                            </button>
                            <button
                              data-testid={`pending-mark-withdrawn-${r.handle}`}
                              disabled={marking === r.handle}
                              onClick={() => markPaid({ handle: r.handle, method: "withdrawn", total: r.total })}
                              className="btn-brutal bg-brand-peach text-brand-ink hover:bg-brand-pink hover:text-white !h-9 !px-2 text-xs disabled:opacity-40"
                              title="Marcar como desistência/bloqueio"
                            >
                              <XCircle weight="bold" size={14} /> Desist.
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-brand-cream">
                        <td colSpan={view === "withdrawn" ? 6 : 7} className="p-3">
                          <table className="tbl w-full">
                            <thead>
                              <tr>
                                <th className="w-32">Data</th>
                                <th>Live</th>
                                {view === "withdrawn" && <th>Motivo</th>}
                                <th className="w-16 text-right">Itens</th>
                                <th className="w-28 text-right">Valor</th>
                                <th className="w-64 text-center">{view === "withdrawn" ? "Reverter esta live" : "Baixar esta live"}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.lives.map(lv => {
                                const mkey = `${r.handle}:${lv.live_id}`;
                                const revertKey = `revert:${lv.live_id}:${r.handle}`;
                                const reasons = view === "withdrawn"
                                  ? Array.from(new Set((lv.items || []).map(it => it.withdrawn_reason).filter(Boolean)))
                                  : [];
                                return (
                                  <tr key={lv.live_id}>
                                    <td className="text-sm">{lv.live_date ? fmtDate(lv.live_date) : "—"}</td>
                                    <td className="font-bold text-sm">{lv.live_name}</td>
                                    {view === "withdrawn" && (
                                      <td className="text-xs text-brand-ink2 italic">
                                        {reasons.length ? reasons.join(" · ") : <span className="opacity-60">(sem motivo)</span>}
                                      </td>
                                    )}
                                    <td className="text-right">{lv.count}</td>
                                    <td className="text-right font-bold">{brl(lv.total)}</td>
                                    <td>
                                      {view === "withdrawn" ? (
                                        <div className="flex items-center justify-center">
                                          <button
                                            data-testid={`pending-live-revert-${r.handle}-${lv.live_id}`}
                                            disabled={marking === revertKey}
                                            onClick={() => revertWithdrawn({ handle: r.handle, live_id: lv.live_id, total: lv.total })}
                                            className="btn-brutal bg-brand-mint text-brand-ink hover:bg-[#C7E5D0] !h-8 !px-3 text-xs disabled:opacity-40"
                                          >
                                            <ArrowClockwise weight="bold" size={12} /> Reverter
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="flex items-center justify-center gap-1 flex-wrap">
                                          <button
                                            data-testid={`pending-live-mark-pix-${r.handle}-${lv.live_id}`}
                                            disabled={marking === mkey}
                                            onClick={() => markPaid({ handle: r.handle, live_id: lv.live_id, method: "pix", total: lv.total })}
                                            className="btn-brutal bg-brand-mint text-brand-ink hover:bg-[#C7E5D0] !h-8 !px-2 text-xs disabled:opacity-40"
                                          >
                                            <Money weight="bold" size={12} /> Pix
                                          </button>
                                          <button
                                            data-testid={`pending-live-mark-credit-${r.handle}-${lv.live_id}`}
                                            disabled={marking === mkey}
                                            onClick={() => markPaid({ handle: r.handle, live_id: lv.live_id, method: "credit", total: lv.total })}
                                            className="btn-brutal bg-brand-yellow text-brand-ink hover:bg-brand-peach !h-8 !px-2 text-xs disabled:opacity-40"
                                          >
                                            <CreditCard weight="bold" size={12} /> Cartão
                                          </button>
                                          <button
                                            data-testid={`pending-live-mark-withdrawn-${r.handle}-${lv.live_id}`}
                                            disabled={marking === mkey}
                                            onClick={() => markPaid({ handle: r.handle, live_id: lv.live_id, method: "withdrawn", total: lv.total })}
                                            className="btn-brutal bg-brand-peach text-brand-ink hover:bg-brand-pink hover:text-white !h-8 !px-2 text-xs disabled:opacity-40"
                                            title="Marcar como desistência/bloqueio"
                                          >
                                            <XCircle weight="bold" size={12} /> Desist.
                                          </button>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {withdrawModal && (
        <WithdrawReasonModal
          data={withdrawModal}
          onChange={setWithdrawModal}
          onCancel={closeWithdraw}
          onConfirm={confirmWithdraw}
          busy={marking !== null}
        />
      )}
    </div>
  );
}

function Stat({ label, value, bg, icon: Icon }) {
  return (
    <div className={`card-brutal p-3 ${bg} flex items-center gap-3`}>
      <Icon weight="bold" size={22} />
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase leading-tight">{label}</p>
        <p className="font-heading text-xl sm:text-2xl font-black break-words">{value}</p>
      </div>
    </div>
  );
}
